import React, { useState, useEffect, useRef, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import * as XLSX from "xlsx";
import { useTranslation } from 'react-i18next';

const LeaseAgingAnalysisReport = () => {
    const { t } = useTranslation(['reports', 'common']);
    const apiService = new LeaseApiService();
    const gridRef = useRef();
    const { currencyPrecision } = useCurrency();
    const [allRows, setAllRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [dynamicColumns, setDynamicColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [formData, setFormData] = useState({
        property: { label: "All", value: "All" },
        unitNo: "",
        asOf: "",
    })
    const formatDate = (date) => {
        return moment(date).format("DD-MMM-YYYY");
    };
    useEffect(() => {
        fetchDropdownData();
        setFormData((prev) => ({
            ...prev,
            asOf: formatDate(new Date()),
        }));
    }, []);

    const dateComparator = (date1, date2) => {
        const momentDate1 = moment(date1);
        const momentDate2 = moment(date2);
        return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
    };
    const dateFormatter = (params) => {
        if (!params.value) return '';
        const date = moment(params.value);
        return date.isValid() ? date.format('DD-MMM-YYYY') : '';
    };
    const amountFormatter = (params) => {
        if (params?.value === null || params?.value === undefined) return '';
        return Number(params.value).toFixed(currencyPrecision?.decimalNumbers || 2);
    };

    const fetchDropdownData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchProperties,');
            const res = await apiService.getApiParamater('REPORT_MULTI_DROP', `?${params.toString()}`);
            setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }

    // Backend returns one row per (lease, aging bucket) via colOrder/colName/amount.
    // Pivot those bucket rows into one row per lease, with a column per distinct colName.
    const pivotAgingData = (data) => {
        const groups = {};
        const bucketMap = {};
        data.forEach((row) => {
            const key = `${row.leaseNo}__${row.customerNo}`;
            if (!groups[key]) {
                groups[key] = {
                    customerNo: row.customerNo,
                    customerName: row.customerName,
                    leaseNo: row.leaseNo,
                    propertyName: row.propertyName,
                    unitNo: row.unitNo,
                    status: row.status,
                    dueDate: row.dueDate,
                    overDuedaysCnt: row.overDuedaysCnt,
                    invoiceCount: row.invoiceCount,
                    totalSum: row.totalSum,
                    totalReceived: row.totalReceived,
                    outstanding: row.outstanding,
                };
            }
            if (row.colName) {
                groups[key][row.colName] = row.amount;
                bucketMap[row.colName] = row.colOrder;
            }
        });
        const bucketColumns = Object.keys(bucketMap)
            .sort((a, b) => bucketMap[a] - bucketMap[b])
            .map((name) => ({ name, order: bucketMap[name] }));
        return { rows: Object.values(groups), bucketColumns };
    };

    const getItems = async () => {
        try {
            setLoader(true);
            const params = new URLSearchParams();
            params.append("AsOn", formData?.asOf || formatDate(new Date()));
            params.append("PropertyName", formData?.property?.label || "All");
            params.append("UnitNo", formData?.unitNo?.label || "All");
            params.append("BasedOn", "0");
            const res = await apiService.getApiParamater("REPORT_AGING_ANALYSIS", `?${params.toString()}`);
            const raw = Array.isArray(res?.data) ? res.data : (res?.data?.result || []);
            if (raw?.length) {
                const { rows, bucketColumns } = pivotAgingData(raw);
                setAllRows(rows);
                setDynamicColumns(bucketColumns);
                setCurrentPage(1);
            } else {
                setAllRows([]);
                setDynamicColumns([]);
                setCurrentPage(1);
                if (res?.data?.message) toast.error(res.data.message);
            }
            setLoader(false);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoader(false);
            setLoading(false);
        }
    }

    const columnDefs = useMemo(() => {
        const base = [
            { headerName: t('reports:leaseAgingAnalysis.columns.customerNo'), field: "customerNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130, pinned: "left" },
            { headerName: t('reports:leaseAgingAnalysis.columns.customerName'), field: "customerName", filter: true, floatingFilter: true, suppressMovable: true, width: 180 },
            { headerName: t('reports:leaseAgingAnalysis.columns.leaseNo'), field: "leaseNo", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
            { headerName: t('reports:leaseAgingAnalysis.columns.propertyName'), field: "propertyName", filter: true, floatingFilter: true, suppressMovable: true, width: 170 },
            { headerName: t('reports:leaseAgingAnalysis.columns.unitNo'), field: "unitNo", filter: true, floatingFilter: true, suppressMovable: true, width: 110 },
            { headerName: t('reports:leaseAgingAnalysis.columns.status'), field: "status", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
            {
                headerName: t('reports:leaseAgingAnalysis.columns.dueDate'), field: "dueDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
                valueFormatter: dateFormatter,
                comparator: dateComparator,
            },
            { headerName: t('reports:leaseAgingAnalysis.columns.overdueDays'), field: "overDuedaysCnt", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
            { headerName: t('reports:leaseAgingAnalysis.columns.invoiceCount'), field: "invoiceCount", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
            {
                headerName: t('reports:leaseAgingAnalysis.columns.totalSum'), field: "totalSum", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
                valueFormatter: amountFormatter,
            },
            {
                headerName: t('reports:leaseAgingAnalysis.columns.totalReceived'), field: "totalReceived", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
                valueFormatter: amountFormatter,
            },
            {
                headerName: t('reports:leaseAgingAnalysis.columns.outstanding'), field: "outstanding", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
                valueFormatter: amountFormatter,
            },
        ];
        const buckets = dynamicColumns.map((col) => ({
            headerName: col.name,
            colId: col.name,
            valueGetter: (params) => params.data?.[col.name],
            filter: true, floatingFilter: true,
            suppressMovable: true,
            width: 130,
            valueFormatter: amountFormatter,
        }));
        return [...base, ...buckets];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicColumns, currencyPrecision]);

    const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));
    const pagination = {
        currentPage,
        totalPages,
        totalRecords: allRows.length,
    };
    const rowData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return allRows.slice(start, start + pageSize);
    }, [allRows, currentPage, pageSize]);
    const changePage = (pageNo) => {
        const clamped = Math.min(Math.max(pageNo, 1), totalPages);
        setCurrentPage(clamped);
    }
    const handleClearFilters = () => {
        setUnitList([]);
        setAllRows([]);
        setCurrentPage(1);
        setDynamicColumns([]);
        setFormData({
            property: { label: "All", value: "All" },
            unitNo: "",
            asOf: formatDate(new Date()),
        });
    }
    const handleDropDownChange = async ({ name, value }) => {
        if (name === "property") {
            setFormData(prev => ({ ...prev, unitNo: null }));
            const propertyValue = value?.value === 'All' ? null : value?.value;
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LRAgreehUnitNosByPropertyId');
            params.append('CascadValue', propertyValue?.value);
            const res = await apiService.getApiParamater('REPORT_CASCAD', `?${params.toString()}`);
            setUnitList(res?.data?.items || []);
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        const updatedValue = typeof value === 'string' ? value : formatDate(value);
        let updatedDates = { ...formData, [name]: updatedValue };
        setFormData(updatedDates);
    }
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return "";
        if (value === 0 || value === "0") return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return moment(value).format("DD-MMM-YYYY");
        }

        return value;
    };
    const exportColumns = useMemo(() => {
        const base = [
            { header: "Customer No", dataKey: "customerNo" },
            { header: "Customer Name", dataKey: "customerName" },
            { header: "Lease No", dataKey: "leaseNo" },
            { header: "Property Name", dataKey: "propertyName" },
            { header: "Unit No", dataKey: "unitNo" },
            { header: "Status", dataKey: "status" },
            { header: "Due Date", dataKey: "dueDate" },
            { header: "Overdue Days", dataKey: "overDuedaysCnt" },
            { header: "Invoice Count", dataKey: "invoiceCount" },
            { header: "Total Sum", dataKey: "totalSum" },
            { header: "Total Received", dataKey: "totalReceived" },
            { header: "Outstanding", dataKey: "outstanding" },
        ];
        const buckets = dynamicColumns.map((col) => ({ header: col.name, dataKey: col.name }));
        return [...base, ...buckets];
    }, [dynamicColumns]);
    const exportPDF = () => {
        if (!allRows?.length) return;

        const doc = new jsPDF("landscape", "pt", "A4");

        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");

        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Lease Aging Analysis Report", 40, 32);

        doc.setFontSize(9);
        doc.text(
            `As of: ${formData?.asOf}`,
            doc.internal.pageSize.width - 160,
            32
        );

        doc.setTextColor(0);

        doc.autoTable({
            startY: 60,
            head: [exportColumns.map(col => col.header)],
            body: allRows.map(row =>
                exportColumns.map(col => normalizeValue(row[col.dataKey]))
            ),
            styles: {
                fontSize: 8,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {
                fillColor: [110, 81, 153],
                textColor: 255,
                fontStyle: "bold",
                halign: "center",
            },
            bodyStyles: {
                halign: "center",
            },
            alternateRowStyles: {
                fillColor: [240, 237, 245],
            },
            didDrawPage: data => {
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: "center" }
                );
            },
        });
        doc.save("LeaseAgingAnalysisReport.pdf");
    };
    const exportExcel = () => {
        if (!allRows?.length) return;

        const excelData = allRows.map(item => {
            const row = {};
            exportColumns.forEach(col => {
                row[col.header] = normalizeValue(item[col.dataKey]);
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Lease Aging Analysis");
        XLSX.writeFile(workbook, "Lease_Aging_Analysis_Report.xlsx");
    };
    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div
                    className="d-flex mt-3 cursor-pointer slide-header"
                    onClick={() => setFilterTab(!FilterTab)}
                >
                    <div className="h6 pt-1">{t('common:searchParameters')}</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain ">
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:property')} </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.property}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
                                    options={propertyList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:unit')} </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitNo', value: selectedOption })}
                                    options={unitList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">{t('reports:asOf')}</label>
                                <DatePicker
                                    selected={formData.asOf}
                                    onChange={(date) => handleDateChange({ name: 'asOf', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                        </div>
                        <div className="form-group grid-contain mt-3">
                            <div className="d-flex mt-2 gap-1">
                                {loader === true ?
                                    <div className='mainbtnGry flex-end gap-2 w-50'>
                                        <ClipLoader color="#ffffff" className='mt-1' size={25} />
                                    </div>
                                    :
                                    <div
                                        onClick={() => getItems()}
                                        className="mainbtnGry flex-end gap-2 w-50"
                                    >
                                        <FaFileCircleExclamation
                                            color="#fff"
                                            className="center mt-2 mx-2"
                                        />
                                        <div className="center">{t('common:search')}</div>
                                    </div>
                                }
                                <div
                                    onClick={() => handleClearFilters()}
                                    className="mainbtnRed flex-end gap-2 w-50"
                                >
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">{t('common:reset')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>{t('common:loading')}</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between">
                            <h5 className="col mainInvHead">{t('reports:leaseAgingAnalysis.title')}</h5>
                            <button className="btn btn-primary" onClick={exportPDF}>
                                {t('reports:exportAsPdf')}
                            </button>
                            <button className="btn btn-secondary mx-3" onClick={exportExcel}>
                                {t('reports:exportAsExcel')}
                            </button>
                        </div>

                        <div className="gridshad mt-3">
                            <div className="ag-theme-quartz" style={{ height: 650 }}>
                                <AgGridReact
                                    ref={gridRef}
                                    getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: "#F0EDF5" } : { background: "#fff" })}
                                    rowData={rowData}
                                    enableFilter={true}
                                    floatingFilter={true}
                                    columnDefs={columnDefs}
                                />
                            </div>
                            <Pagination
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pagination={pagination}
                                getItems={changePage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};
export default LeaseAgingAnalysisReport;
